import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import useIsMobile from '../../hooks/useIsMobile';

const SpendingBreakdown = ({ data }) => {
  const isMobile = useIsMobile();
  const COLORS = ['#007AFF', '#5856D6', '#FF2D55', '#FF9500', '#34C759', '#AF52DE', '#FF6B35', '#32D74B'];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <View style={styles.tooltip}>
          <Text style={styles.tooltipTitle}>{data.name}</Text>
          <Text style={styles.tooltipAmount}>{formatCurrency(data.amount)}</Text>
          <Text style={styles.tooltipPercent}>{data.percentage}% of total</Text>
        </View>
      );
    }
    return null;
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Spending Breakdown</Text>
      
      <View style={[styles.content, isMobile && styles.mobileContent]}>
        <View style={[styles.chartContainer, isMobile && styles.mobileChart]}>
          <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
            <PieChart>
              <Pie
                data={data.categories}
                dataKey="percentage"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={isMobile ? 80 : 100}
                innerRadius={isMobile ? 20 : 30}
                fill="#8884d8"
                label={false}
                strokeWidth={2}
                stroke="#ffffff"
              >
                {data.categories.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </View>

        <View style={[styles.legendContainer, isMobile && styles.mobileLegend]}>
          {data.categories.map((category, index) => (
            <View key={category.name} style={styles.legendItem}>
              <View style={styles.legendLeft}>
                <View
                  style={[
                    styles.legendColor,
                    { backgroundColor: COLORS[index % COLORS.length] }
                  ]}
                />
                <Text style={styles.legendText}>{category.name}</Text>
              </View>
              <View style={styles.legendRight}>
                <Text style={styles.legendAmount}>
                  {formatCurrency(category.amount)}
                </Text>
                <Text style={styles.legendPercent}>{category.percentage}%</Text>
              </View>
            </View>
          ))}
          
          <View style={styles.legendTotal}>
            <View style={styles.legendLeft}>
              <Text style={styles.legendTotalText}>Total</Text>
            </View>
            <View style={styles.legendRight}>
              <Text style={styles.legendTotalAmount}>
                {formatCurrency(data.total)}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 16,
  },
  content: {
    flexDirection: 'row',
    gap: 24,
  },
  mobileContent: {
    flexDirection: 'column',
    gap: 16,
  },
  chartContainer: {
    flex: 1,
    height: 300,
  },
  mobileChart: {
    height: 250,
  },
  legendContainer: {
    flex: 1,
    gap: 12,
  },
  mobileLegend: {
    gap: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  legendLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  legendText: {
    fontSize: 14,
    color: '#6B7280',
    flex: 1,
  },
  legendRight: {
    alignItems: 'flex-end',
  },
  legendAmount: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  legendPercent: {
    fontSize: 12,
    color: '#6B7280',
  },
  legendTotal: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    marginTop: 4,
  },
  legendTotalText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  legendTotalAmount: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  tooltip: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#F3F4F6',
  },
  tooltipTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  tooltipAmount: {
    fontSize: 14,
    color: '#6B7280',
  },
  tooltipPercent: {
    fontSize: 14,
    color: '#6B7280',
  },
});

export default SpendingBreakdown; 